// index.tsx
import React, { useState, useCallback, useEffect } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import {
  useMutation,
  useQuery,
  useLazyQuery,
  useApolloClient,
} from "@apollo/client";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import { isEqual } from "lodash";

import ShareClassRowForm from "./ShareClassRowForm";
import {
  ADD_SHARE_CLASS,
  ADD_TEMP_FILES,
  GET_SHARE_CLASS_PROFILE,
  GET_FEE_CALC_ENUM,
  GET_FEE_FREQ_ENUM,
  GET_PE_MGMT_FEE_CALC_ENUM,
  GET_CALENDAR_UNIT_ENUM,
  GET_LOCKUP_END_VALUE_ENUM,
  GET_RIGHT_OF_EXTENSION_ENUM,
  GET_COMPOUNDING_PERIOD_ENUM,
  GET_WORKOUT_ENUM,
  GET_CURRENCY_DENOMINATION_ENUM,
  GET_PE_INCT_FEE_CALC_ENUM,
  GET_EXISTING_SHARE_CLASSES,
  UPDATE_SHARE_CLASS,
} from "./graphql";
import {
  FormValues,
  ExtractedShareClass,
  BulkShareClassExtractionProps,
  TempFile,
} from "./types";
import { buildShareClassInput } from "./buildShareClassInput";

const BulkShareClassExtraction: React.FC<BulkShareClassExtractionProps> = ({
  investmentId,
  investmentName,
  marketEnumId,
  onSuccess,
}) => {
  const apolloClient = useApolloClient();
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error" | "info";
    open: boolean;
  }>({ message: "", type: "info", open: false });

  const [tempFiles, setTempFiles] = useState<TempFile[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<string[]>([]);

  // Form methods
  const methods = useForm<FormValues>({
    defaultValues: {
      shareClasses: [],
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    resetField,
    getValues,
    formState: { errors },
  } = methods;

  const { fields, remove, append } = useFieldArray({
    control,
    name: "shareClasses",
  });

  // Load all enum data
  const { data: feeCalcData } = useQuery(GET_FEE_CALC_ENUM);
  const { data: feeFreqData } = useQuery(GET_FEE_FREQ_ENUM);
  const { data: peMgmtFeeCalcData } = useQuery(GET_PE_MGMT_FEE_CALC_ENUM);
  const { data: peInctFeeCalcData } = useQuery(GET_PE_INCT_FEE_CALC_ENUM);
  const { data: calendarUnitData } = useQuery(GET_CALENDAR_UNIT_ENUM);
  const { data: lockupEndValueData } = useQuery(GET_LOCKUP_END_VALUE_ENUM);
  const { data: rightOfExtensionData } = useQuery(GET_RIGHT_OF_EXTENSION_ENUM);
  const { data: compoundingPeriodData } = useQuery(GET_COMPOUNDING_PERIOD_ENUM);
  const { data: workoutData } = useQuery(GET_WORKOUT_ENUM);
  const { data: currencyData } = useQuery(GET_CURRENCY_DENOMINATION_ENUM);

  // Mutations
  const [addTempFiles] = useMutation(ADD_TEMP_FILES);
  const [getShareClassProfile] = useLazyQuery(GET_SHARE_CLASS_PROFILE, {
    fetchPolicy: "no-cache",
  });
  const [getExistingShareClasses] = useLazyQuery(GET_EXISTING_SHARE_CLASSES, {
    fetchPolicy: "no-cache",
  });
  const [addShareClass] = useMutation(ADD_SHARE_CLASS);
  const [updateShareClass] = useMutation(UPDATE_SHARE_CLASS);

  // Currency options are already filtered by isPerformanceCurrency in the GraphQL query
  const currencyOptions =
    currencyData?.currencyDenominationEnumList?.items || [];

  // Helper to find changed fields between extracted and existing share class
  const findChangedFields = (
    extracted: ExtractedShareClass,
    existing: any,
    basePath: string
  ): string[] => {
    const changedFields: string[] = [];

    const compare = (extractedObj: any, existingObj: any, path: string) => {
      if (extractedObj === null || extractedObj === undefined) return;

      if (typeof extractedObj === "object" && !Array.isArray(extractedObj)) {
        Object.keys(extractedObj).forEach(key => {
          // Skip internal tracking fields
          if (key === "id" || key === "existingId" || key === "isExisting")
            return;

          const extractedValue = extractedObj[key];
          const existingValue = existingObj?.[key];
          const fieldPath = `${path}.${key}`;

          if (extractedValue !== null && extractedValue !== undefined) {
            if (
              typeof extractedValue === "object" &&
              !Array.isArray(extractedValue)
            ) {
              // Recursively compare nested objects
              compare(extractedValue, existingValue, fieldPath);
            } else {
              // Compare leaf values - only mark as changed if different
              if (!isEqual(extractedValue, existingValue)) {
                changedFields.push(fieldPath);
              }
            }
          }
        });
      }
    };

    compare(extracted, existing, basePath);
    return changedFields;
  };

  // Handle file upload and AI extraction
  const handleFileUpload = async (files: File[]) => {
    if (!files || files.length === 0) return;

    setIsExtracting(true);
    try {
      // Upload files to temp storage
      const tempFileInputs = files.map(file => ({ file }));
      const { data: tempData } = await addTempFiles({
        variables: { input: tempFileInputs },
      });

      if (!tempData?.addTempFiles) {
        throw new Error("Failed to upload files");
      }

      const uploadedFiles: TempFile[] = tempData.addTempFiles.map(
        (tf: any, idx: number) => ({
          file: files[idx],
          filename: tf.filename,
          key: tf.key,
          bucket: tf.bucket,
        })
      );

      setTempFiles(uploadedFiles);

      // Extract share class profiles using AI
      const { data: profileData } = await getShareClassProfile({
        variables: {
          bucket: uploadedFiles[0].bucket,
          Keys: uploadedFiles.map(f => f.key!),
          investmentId,
        },
      });

      if (!profileData?.shareClassProfileBuilder?.shareClassProfiles) {
        throw new Error("No share class profiles extracted");
      }

      const profiles = profileData.shareClassProfileBuilder.shareClassProfiles;

      // Fetch existing share classes for this investment
      const existingResult = await getExistingShareClasses({
        variables: {
          filter: { investmentId },
        },
      });

      const existingShareClasses =
        existingResult?.data?.shareClassList?.items || [];

      // Match extracted profiles with existing share classes by name
      // The LLM is already instructed to use exact names from existing share classes,
      // so we do simple case-insensitive matching here
      const matchedProfiles: ExtractedShareClass[] = [];
      const autoFields: string[] = [];

      profiles.forEach((profile: ExtractedShareClass, index: number) => {
        // Find matching existing share class by name (case-insensitive)
        const existingMatch = existingShareClasses.find(
          (existing: any) =>
            existing.name?.toLowerCase().trim() ===
            profile.name?.toLowerCase().trim()
        );

        if (existingMatch) {
          // Found a match - mark as existing and only highlight changed fields
          const matchedProfile: ExtractedShareClass = {
            ...profile,
            existingId: existingMatch.id,
            isExisting: true,
          };
          matchedProfiles.push(matchedProfile);

          // Only highlight fields that have actually changed
          const changedFields = findChangedFields(
            profile,
            existingMatch,
            `shareClasses.${index}`
          );
          autoFields.push(...changedFields);
        } else {
          // No match - this is a new share class, highlight all auto-populated fields
          matchedProfiles.push({
            ...profile,
            isExisting: false,
          });

          // Track all auto-populated fields for new share classes
          const trackFields = (obj: any, basePath: string) => {
            if (obj === null || obj === undefined) return;

            if (typeof obj === "object" && !Array.isArray(obj)) {
              Object.keys(obj).forEach(key => {
                // Skip internal tracking fields
                if (key === "id" || key === "existingId" || key === "isExisting")
                  return;

                const value = obj[key];
                const fieldPath = `${basePath}.${key}`;

                if (value !== null && value !== undefined) {
                  if (typeof value === "object" && !Array.isArray(value)) {
                    trackFields(value, fieldPath);
                  } else {
                    autoFields.push(fieldPath);
                  }
                }
              });
            }
          };

          trackFields(profile, `shareClasses.${index}`);
        }
      });

      // Set form values with matched profiles
      setValue("shareClasses", matchedProfiles);
      setAutoPopulatedFields(autoFields);

      const existingCount = matchedProfiles.filter(p => p.isExisting).length;
      const newCount = matchedProfiles.length - existingCount;

      let message = `Successfully extracted ${matchedProfiles.length} share ${
        matchedProfiles.length === 1 ? "class" : "classes"
      }`;
      if (existingCount > 0) {
        message += ` (${existingCount} matching existing, ${newCount} new)`;
      }

      setNotification({
        message,
        type: "success",
        open: true,
      });
    } catch (error) {
      setNotification({
        message: "Failed to extract share classes from documents",
        type: "error",
        open: true,
      });
    } finally {
      setIsExtracting(false);
    }
  };

  // Handle publishing all share classes
  const onSubmit = async (data: FormValues) => {
    setIsPublishing(true);
    try {
      let createCount = 0;
      let updateCount = 0;
      const failures: string[] = [];
      const savedRows: number[] = [];

      for (const [index, shareClass] of data.shareClasses.entries()) {
        const input = buildShareClassInput(shareClass, investmentId);

        try {
          if (shareClass.isExisting && shareClass.existingId) {
            await updateShareClass({
              variables: { id: shareClass.existingId, input },
            });
            updateCount++;
          } else {
            await addShareClass({ variables: { input } });
            createCount++;
          }
          savedRows.push(index);
        } catch (error) {
          failures.push(
            `${shareClass.name || `Share class ${index + 1}`}: ${
              (error as Error).message
            }`
          );
        }
      }

      const totalSuccess = createCount + updateCount;
      if (totalSuccess > 0) {
        let message = "";
        if (createCount > 0 && updateCount > 0) {
          message = `Successfully created ${createCount} and updated ${updateCount} share ${
            totalSuccess === 1 ? "class" : "classes"
          }`;
        } else if (createCount > 0) {
          message = `Successfully created ${createCount} share ${
            createCount === 1 ? "class" : "classes"
          }`;
        } else {
          message = `Successfully updated ${updateCount} share ${
            updateCount === 1 ? "class" : "classes"
          }`;
        }

        setNotification({
          message,
          type: "success",
          open: true,
        });

        // Refetch all queries to ensure the investment terms page shows updated data
        await apolloClient.refetchQueries({
          include: ["getListShareClass"],
        });
      }

      if (failures.length > 0) {
        // Keep the failed rows on screen so the edits are not lost - dropping
        // the saved ones, otherwise a second Save re-creates them - and report
        // what the server actually said rather than just a count.
        remove(savedRows);
        setNotification({
          message: `Failed to save ${failures.length} share ${
            failures.length === 1 ? "class" : "classes"
          } - ${failures.join(" | ")}`,
          type: "error",
          open: true,
        });
        return;
      }

      // Clear form
      setValue("shareClasses", []);
      setTempFiles([]);
      setAutoPopulatedFields([]);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setNotification({
        message: `Failed to save share classes: ${(error as Error).message}`,
        type: "error",
        open: true,
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClearAll = () => {
    setValue("shareClasses", []);
    setTempFiles([]);
    setAutoPopulatedFields([]);
  };

  return (
    <FormProvider {...methods}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            AI Bulk Share Class Extraction
          </Typography>

          {fields.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a terms document (e.g., PPM, term sheet, offering
              memorandum) to automatically extract share class terms. The AI
              will identify multiple share classes if present (e.g., Class A,
              Class I, Institutional, etc.).
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Review Extracted Share Classes</AlertTitle>
              Review the extracted share classes below. Share classes marked
              &quot;Update&quot; match existing records by name and will update
              them. Share classes marked &quot;New&quot; will be created. Fields
              with a purple background indicate changes from existing values or
              new AI-extracted data.
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <input
              type="file"
              id="share-class-file-upload"
              style={{ display: "none" }}
              accept=".pdf"
              multiple
              onChange={e => {
                if (e.target.files) {
                  handleFileUpload(Array.from(e.target.files));
                }
              }}
            />
            <label htmlFor="share-class-file-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<AutoAwesomeIcon />}
                disabled={isExtracting || fields.length > 0}
              >
                {isExtracting ? "Extracting..." : "AI Extract Share Classes"}
              </Button>
            </label>

            {isExtracting && <CircularProgress size={24} />}
          </Box>
        </Box>

        {fields.length > 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Extracted Share Classes ({fields.length})
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleClearAll}
                  disabled={isPublishing}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    isPublishing ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  onClick={handleSubmit(onSubmit)}
                  disabled={isPublishing}
                >
                  {isPublishing ? "Saving..." : "Save All Share Classes"}
                </Button>
              </Box>
            </Box>

            {fields.map((field, index) => (
              <ShareClassRowForm
                key={field.id}
                index={index}
                control={control}
                setValue={setValue}
                resetField={resetField}
                watch={watch}
                errors={errors}
                onRemove={remove}
                autoPopulatedFields={autoPopulatedFields}
                setAutoPopulatedFields={setAutoPopulatedFields}
                marketEnumId={marketEnumId}
                feeCalcOptions={feeCalcData?.feeCalcEnumList?.items || []}
                feeFreqOptions={feeFreqData?.feeFreqEnumList?.items || []}
                peMgmtFeeCalcOptions={
                  peMgmtFeeCalcData?.peMgmtFeeCalcEnumList?.items || []
                }
                peInctFeeCalcOptions={
                  peInctFeeCalcData?.peInctFeeCalcEnumList?.items || []
                }
                calendarUnitOptions={
                  calendarUnitData?.calendarUnitEnumList?.items || []
                }
                lockupEndValueOptions={
                  lockupEndValueData?.lockupEndValueEnumList?.items || []
                }
                rightOfExtensionOptions={
                  rightOfExtensionData?.rightOfExtensionEnumList?.items || []
                }
                compoundingPeriodOptions={
                  compoundingPeriodData?.compoundingPeriodEnumList?.items || []
                }
                workoutOptions={workoutData?.workoutEnumList?.items || []}
                currencyOptions={currencyOptions}
              />
            ))}
          </Box>
        )}

        <Snackbar
          open={notification.open}
          autoHideDuration={notification.type === "error" ? null : 6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={notification.type}
            onClose={() => setNotification({ ...notification, open: false })}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </FormProvider>
  );
};

export default BulkShareClassExtraction;
